import type { usePosLotAllocation } from '@/hooks/use-pos-lot-allocation'
import { LotAllocationEditor } from '@/components/sections/lot-allocation-editor'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    lots: ReturnType<typeof usePosLotAllocation>
    /** Tab đang khoá chờ thanh toán CK → không cho đổi lô. */
    disabled?: boolean
    /**
     * Lỗi hiện dưới khối, do panel checkout đặt khi thu ngân bấm bán mà chưa
     * xác nhận bán nợ. Nút submit KHÔNG bị disable — bấm được, rồi thấy lỗi.
     */
    submitError?: string | null
    /**
     * Luồng này có được bán nợ lô không. CHỈ "Bán nhanh" tại quầy được — backend
     * chặn cứng theo is_quick_sale. Bật ở tab giao hàng / bán thường sẽ làm nút
     * sáng lên rồi bấm mới bị từ chối, thu ngân không hiểu vì sao.
     */
    allowDebt?: boolean
}

/**
 * Khối "Lô xuất kho" trong panel checkout POS: gợi ý FIFO đã seed, thu ngân
 * đổi lô nếu khách lấy lô khác trên kệ. Dùng chung editor với popup xuất kho.
 */
export function PosLotSection({
    lots,
    disabled,
    submitError,
    allowDebt = false,
}: Props) {
    if (lots.isLoading) {
        return (
            <p className="text-xs text-muted-foreground">
                {m.pos_lot_loading()}
            </p>
        )
    }
    // Đang thiếu hàng và thu ngân chưa tick xác nhận — nguyên nhân duy nhất của
    // lỗi phân bổ lúc này, nên không cần báo thêm lần nữa.
    const pendingDebtConfirm =
        allowDebt && lots.debtQty > 0 && !lots.debtConfirmed

    if (lots.isEmpty) return null
    return (
        <div
            className={disabled ? 'pointer-events-none opacity-60' : undefined}
            data-testid="pos-lot-section"
        >
            <div className="mb-1">
                <span className="text-xs font-semibold">
                    {m.pos_lot_section_title()}
                </span>
                <p className="text-xs text-muted-foreground">
                    {m.pos_lot_section_hint()}
                </p>
            </div>
            <LotAllocationEditor
                compact
                allowShortfall={allowDebt && lots.debtConfirmed}
                lines={lots.lines}
                matLines={lots.matLines}
                warehouseId={lots.warehouseId}
                onLinesChange={lots.setLines}
                onMatLinesChange={lots.setMatLines}
            />
            {allowDebt && lots.debtQty > 0 && (
                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 dark:border-amber-800/60 dark:bg-amber-950/40">
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                        {m.pos_lot_debt_warning_intro()}
                    </p>
                    <ul className="mt-0.5 space-y-0.5">
                        {lots.debtLines.map((d) => (
                            <li
                                key={d.productName}
                                className="text-xs font-medium text-amber-900 dark:text-amber-200"
                            >
                                {m.pos_lot_debt_warning_line({
                                    name: d.productName,
                                    qty: formatQty(d.qty),
                                    unit: d.unit,
                                })}
                            </li>
                        ))}
                    </ul>
                    <label className="mt-1.5 flex cursor-pointer items-center gap-2">
                        <input
                            type="checkbox"
                            checked={lots.debtConfirmed}
                            onChange={(e) =>
                                lots.setDebtConfirmed(e.target.checked)
                            }
                            aria-label={m.pos_lot_debt_confirm_label()}
                            className="size-4 accent-primary"
                        />
                        <span className="text-xs font-medium">
                            {m.pos_lot_debt_confirm_label()}
                        </span>
                    </label>
                </div>
            )}
            {/* Thiếu hàng chưa xác nhận thì khối hổ phách bên trên đã nói rõ rồi.
                Hiện thêm "phân bổ chưa khớp" là thừa và sai nguyên nhân — thu
                ngân không gõ sai số, kho hết hàng thật. */}
            {lots.hasError && !pendingDebtConfirm && (
                <p className="mt-1 text-xs text-destructive">
                    {m.pos_lot_has_error()}
                </p>
            )}
            {submitError && (
                <p className="mt-1 text-xs text-destructive">{submitError}</p>
            )}
        </div>
    )
}
